import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  Avatar,
  Divider,
  Autocomplete,
  Container,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from "@mui/material";
import {
  Edit,
  Business,
  Email,
  Phone,
  LocationOn,
  Person,
  Badge,
  CheckCircle,
  Cancel,
  Save,
} from "@mui/icons-material";
import { EnterprisesAPI } from "../api/enterprises";
import LocationsAPI from "../api/locations";
import TopBar from "../components/TopBar";

export default function EnterpriseProfile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "info" });
  const [data, setData] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  // Location dropdowns
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const openSnack = (msg, sev = "info") => setSnack({ open: true, msg, sev });

  const load = async () => {
    try {
      setLoading(true);
      const res = await EnterprisesAPI.getMyEnterprise();
      setData(res?.data?.data || {});
    } catch (err) {
      openSnack("Failed to load enterprise profile", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadCountries();
  }, []);

  useEffect(() => {
    if (data.country) {
      loadStates(data.country);
    }
  }, [data.country]);

  useEffect(() => {
    if (data.country && data.state) {
      loadCities(data.country, data.state);
    }
  }, [data.state]);

  const loadCountries = async () => {
    try {
      setLoadingLocations(true);
      const res = await LocationsAPI.getCountries();
      setCountries(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load countries:", err);
    } finally {
      setLoadingLocations(false);
    }
  };

  const loadStates = async (countryCode) => {
    try {
      setLoadingLocations(true);
      const res = await LocationsAPI.getStates(countryCode);
      setStates(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load states:", err);
      setStates([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  const loadCities = async (countryCode, stateCode) => {
    try {
      setLoadingLocations(true);
      const res = await LocationsAPI.getCities(countryCode, stateCode);
      setCities(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load cities:", err);
      setCities([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleChange = (field) => (e) => {
    setEditData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleLocationChange = (field, value) => {
    if (field === "country") {
      setEditData((prev) => ({ ...prev, country: value, state: "", city: "" }));
      setStates([]);
      setCities([]);
    } else if (field === "state") {
      setEditData((prev) => ({ ...prev, state: value, city: "" }));
      setCities([]);
    } else {
      setEditData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleConsentChange = (e) => {
    setEditData((prev) => ({ ...prev, gdpr_consent: e.target.checked }));
  };

  const handleEdit = () => {
    setEditData({ ...data });
    setEditMode(true);
  };

  const handleCancel = () => {
    setEditData({});
    setEditMode(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await EnterprisesAPI.updateMyEnterprise({
        name: editData.name,
        email: editData.email,
        company_type: editData.company_type,
        company_field: editData.company_field,
        address_line1: editData.address_line1,
        street: editData.street,
        landmark: editData.landmark,
        country: editData.country,
        state: editData.state,
        city: editData.city,
        pincode: editData.pincode,
        poc_name: editData.poc_name,
        poc_email: editData.poc_email,
        poc_phone: editData.poc_phone,
        poc_position: editData.poc_position,
        gdpr_consent: editData.gdpr_consent,
      });
      openSnack("Profile updated successfully", "success");
      setData({ ...editData });
      setEditMode(false);
    } catch (err) {
      const msg =
        err?.response?.data?.error || err?.response?.data?.message || err.message;
      openSnack(msg || "Failed to update profile", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TopBar />
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          pt: { xs: 8, sm: 10 },
        }}
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mb: 3,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              borderRadius: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: "rgba(255,255,255,0.2)",
                    fontSize: "2rem",
                    fontWeight: 700,
                    border: "4px solid rgba(255,255,255,0.3)",
                  }}
                >
                  {data?.name?.[0]?.toUpperCase?.() || "C"}
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {data.name || "Company Name"}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label={data.user_status ? data.user_status.toUpperCase() : "FREE"}
                      size="small"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.25)",
                        color: "white",
                        fontWeight: 700,
                        backdropFilter: "blur(10px)",
                      }}
                    />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {data.company_field || "Business"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {!editMode && (
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={handleEdit}
                  sx={{
                    bgcolor: "white",
                    color: "#667eea",
                    fontWeight: 600,
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.9)",
                    },
                  }}
                >
                  Edit Profile
                </Button>
              )}
            </Box>
          </Paper>

          {/* Company Information Cards - View Mode */}
          {!editMode && (
            <Grid container spacing={3}>
              {/* Basic Info Card */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%", borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <Business sx={{ color: "#667eea", mr: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Company Information
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                      <InfoRow icon={<Business />} label="Company Name" value={data.name} />
                      <InfoRow icon={<Email />} label="Email" value={data.email} />
                      <InfoRow icon={<Badge />} label="Company Type" value={data.company_type} />
                      <InfoRow icon={<Business />} label="Industry" value={data.company_field} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Address Card */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%", borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <LocationOn sx={{ color: "#667eea", mr: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Address Details
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                      <InfoRow
                        icon={<LocationOn />}
                        label="Address"
                        value={
                          [data.address_line1, data.street, data.landmark]
                            .filter(Boolean)
                            .join(", ") || "Not provided"
                        }
                      />
                      <InfoRow
                        icon={<LocationOn />}
                        label="Location"
                        value={
                          [data.city, data.state, data.country]
                            .filter(Boolean)
                            .join(", ") || "Not provided"
                        }
                      />
                      <InfoRow icon={<LocationOn />} label="PIN Code" value={data.pincode} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Primary Contact Card */}
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <Person sx={{ color: "#667eea", mr: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Primary Contact Person
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={3}>
                        <InfoRow icon={<Person />} label="Name" value={data.poc_name} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <InfoRow icon={<Email />} label="Email" value={data.poc_email} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <InfoRow icon={<Phone />} label="Phone" value={data.poc_phone} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <InfoRow icon={<Badge />} label="Position" value={data.poc_position} />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* GDPR Consent */}
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {data.gdpr_consent ? (
                        <CheckCircle sx={{ color: "#10b981" }} />
                      ) : (
                        <Cancel sx={{ color: "#ef4444" }} />
                      )}
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {data.gdpr_consent
                          ? "Data processing consent provided"
                          : "Data processing consent not provided"}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Edit Mode Dialog */}
          <Dialog
            open={editMode}
            onClose={handleCancel}
            maxWidth="md"
            fullWidth
            fullScreen={false}
            PaperProps={{
              sx: { 
                borderRadius: 3,
                maxHeight: '90vh',
              },
            }}
          >
            <DialogTitle 
              sx={{ 
                bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 700,
                fontSize: '1.5rem',
                py: 2.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Edit />
                <span>Edit Company Profile</span>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 4, pb: 2 }}>
              {/* Company Information Section */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <Business sx={{ color: '#667eea', fontSize: 28 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Company Information
                  </Typography>
                </Box>
                <Paper elevation={0} sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Company Name"
                        fullWidth
                        required
                        value={editData.name || ""}
                        onChange={handleChange("name")}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Company Email"
                        type="email"
                        fullWidth
                        required
                        value={editData.email || ""}
                        onChange={handleChange("email")}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Company Type"
                        fullWidth
                        placeholder="e.g., Private Limited, LLP"
                        value={editData.company_type || ""}
                        onChange={handleChange("company_type")}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Industry / Business Field"
                        fullWidth
                        placeholder="e.g., IT Services, Manufacturing"
                        value={editData.company_field || ""}
                        onChange={handleChange("company_field")}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* Address Section */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <LocationOn sx={{ color: '#667eea', fontSize: 28 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Address Details
                  </Typography>
                </Box>
                <Paper elevation={0} sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Address Line 1"
                        fullWidth
                        placeholder="Building, Street"
                        value={editData.address_line1 || ""}
                        onChange={handleChange("address_line1")}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Street / Area"
                        fullWidth
                        placeholder="Locality, Neighborhood"
                        value={editData.street || ""}
                        onChange={handleChange("street")}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        options={countries}
                        getOptionLabel={(option) => option.name || option}
                        value={countries.find((c) => c.isoCode === editData.country) || null}
                        onChange={(e, newValue) => {
                          handleLocationChange("country", newValue?.isoCode || "");
                        }}
                        loading={loadingLocations}
                        renderInput={(params) => (
                          <TextField {...params} label="Country" fullWidth placeholder="Select country" />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        options={states}
                        getOptionLabel={(option) => option.name || option}
                        value={states.find((s) => s.isoCode === editData.state) || null}
                        onChange={(e, newValue) => {
                          handleLocationChange("state", newValue?.isoCode || "");
                        }}
                        loading={loadingLocations}
                        disabled={!editData.country || states.length === 0}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="State / Region"
                            fullWidth
                            placeholder={!editData.country ? "Select country first" : "Select state"}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        options={cities}
                        getOptionLabel={(option) => option.name || option}
                        value={cities.find((c) => c.name === editData.city) ? { name: editData.city } : null}
                        onChange={(e, newValue) => {
                          handleLocationChange("city", newValue?.name || "");
                        }}
                        freeSolo
                        loading={loadingLocations}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="City"
                            fullWidth
                            placeholder="Type or select"
                            onChange={(e) => handleLocationChange("city", e.target.value)}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Postal / PIN Code"
                        fullWidth
                        placeholder="e.g., 400001"
                        value={editData.pincode || ""}
                        onChange={handleChange("pincode")}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* Contact Person Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <Person sx={{ color: '#667eea', fontSize: 28 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Primary Contact Person
                  </Typography>
                </Box>
                <Paper elevation={0} sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Contact Name"
                        fullWidth
                        placeholder="Full Name"
                        value={editData.poc_name || ""}
                        onChange={handleChange("poc_name")}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Contact Email"
                        type="email"
                        fullWidth
                        placeholder="email@example.com"
                        value={editData.poc_email || ""}
                        onChange={handleChange("poc_email")}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Contact Phone"
                        fullWidth
                        placeholder="+91 98765 43210"
                        value={editData.poc_phone || ""}
                        onChange={handleChange("poc_phone")}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Position / Role"
                        fullWidth
                        placeholder="e.g., CEO, IT Manager"
                        value={editData.poc_position || ""}
                        onChange={handleChange("poc_position")}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* GDPR Consent */}
              <Paper elevation={0} sx={{ p: 2.5, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #86efac' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(editData.gdpr_consent)}
                      onChange={handleConsentChange}
                      sx={{ color: '#059669' }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#065f46' }}>
                      I agree to the data processing terms for this company
                    </Typography>
                  }
                />
              </Paper>
            </DialogContent>
            <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', gap: 2, borderTop: '1px solid #e2e8f0' }}>
              <Button 
                onClick={handleCancel} 
                disabled={saving}
                variant="outlined"
                sx={{ 
                  color: '#64748b',
                  borderColor: '#cbd5e1',
                  px: 3,
                  '&:hover': {
                    borderColor: '#94a3b8',
                    bgcolor: '#f1f5f9'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? null : <Save />}
                sx={{
                  bgcolor: "#667eea",
                  px: 4,
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#5568d3" },
                }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack({ ...snack, open: false })}
          severity={snack.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

// Helper component for displaying info rows
function InfoRow({ icon, label, value }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500, mb: 0.5, display: "block" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ color: "#94a3b8", fontSize: "1rem" }}>{icon}</Box>
        <Typography variant="body1" sx={{ fontWeight: 500, color: value ? "#1e293b" : "#94a3b8" }}>
          {value || "Not provided"}
        </Typography>
      </Box>
    </Box>
  );
}
